import { apiService } from '../../services/api';
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  BookOpen, 
  Target, 
  Lightbulb,
  SpellCheck
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GrammarError {
  error: string;
  suggestion: string;
  position: string;
  severity: 'low' | 'medium' | 'high';
}

interface StyleIssue {
  issue: string;
  suggestion: string;
  type: 'voice' | 'tone' | 'clarity' | 'flow';
  severity: 'low' | 'medium' | 'high';
}

interface ReadabilityMetrics {
  readingLevel: string;
  averageSentenceLength: number;
  complexWords: number;
  readabilityScore: number;
}

interface GrammarAnalysis {
  grammarErrors: GrammarError[];
  styleIssues: StyleIssue[];
  readabilityMetrics: ReadabilityMetrics;
  strengths: string[];
  overallScore: number;
  personalizedTips: string[];
}

interface GrammarCheckerProps {
  initialText?: string;
  onTextChange?: (text: string) => void;
}

export function GrammarChecker({ initialText = '', onTextChange }: GrammarCheckerProps) {
  const [text, setText] = useState(initialText);
  const [analysis, setAnalysis] = useState<GrammarAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  const analyzeText = async () => {
    if (!text.trim()) {
      toast({
        title: "No Text",
        description: "Please enter some text to analyze",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const data = await apiService.post('/api/tools/grammar-check', {
        text,
        contextType: 'creative_writing'
      });
      setAnalysis(data.analysis);
      toast({
        title: "Analysis Complete",
        description: `Found ${data.analysis.grammarErrors.length} grammar issues and ${data.analysis.styleIssues.length} style suggestions`
      });
    } catch (error) {
      console.error('Error analyzing text:', error);
      toast({
        title: "Error",
        description: "Failed to analyze text",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleTextChange = (newText: string) => {
    setText(newText);
    onTextChange?.(newText);
    // Clear analysis when text changes significantly
    if (analysis && Math.abs(newText.length - text.length) > 10) {
      setAnalysis(null);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-3 w-3" />;
      case 'medium': return <Info className="h-3 w-3" />;
      case 'low': return <CheckCircle className="h-3 w-3" />;
      default: return <Info className="h-3 w-3" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getReadabilityColor = (score: number) => {
    if (score >= 0.7) return 'text-green-600';
    if (score >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Text Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SpellCheck className="h-5 w-5" />
            Grammar & Style Checker
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Paste your text here for grammar and style analysis..."
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            rows={8}
            className="resize-none"
          />
          
          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              {text.length} characters, ~{Math.ceil(text.split(' ').length)} words
            </div>
            
            <Button
              onClick={analyzeText}
              disabled={isAnalyzing || !text.trim()}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze Text'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysis && (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="grammar">
              Grammar ({analysis.grammarErrors.length})
            </TabsTrigger>
            <TabsTrigger value="style">
              Style ({analysis.styleIssues.length})
            </TabsTrigger>
            <TabsTrigger value="readability">Readability</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Analysis Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Overall Score */}
                <div className="text-center space-y-2">
                  <div className="text-3xl font-bold text-primary">
                    {Math.round(analysis.overallScore * 100)}%
                  </div>
                  <div className={`text-lg font-medium ${getScoreColor(analysis.overallScore)}`}>
                    Overall Quality Score
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-red-600">
                      {analysis.grammarErrors.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Grammar Issues</div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-yellow-600">
                      {analysis.styleIssues.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Style Suggestions</div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-green-600">
                      {analysis.strengths.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Strengths</div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className={`text-2xl font-bold ${getReadabilityColor(analysis.readabilityMetrics.readabilityScore)}`}>
                      {Math.round(analysis.readabilityMetrics.readabilityScore * 100)}%
                    </div>
                    <div className="text-sm text-muted-foreground">Readability</div>
                  </div>
                </div>

                {/* Strengths */}
                {analysis.strengths.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-medium flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Strengths
                    </div>
                    <div className="space-y-1">
                      {analysis.strengths.map((strength, index) => (
                        <div key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                          <div className="w-1 h-1 bg-green-600 rounded-full" />
                          {strength}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Personalized Tips */}
                {analysis.personalizedTips.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-medium flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-blue-600" />
                      Personalized Tips
                    </div>
                    <div className="space-y-1">
                      {analysis.personalizedTips.map((tip, index) => (
                        <div key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                          <div className="w-1 h-1 bg-blue-600 rounded-full" />
                          {tip}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="grammar">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Grammar Issues</CardTitle>
              </CardHeader>
              <CardContent>
                {analysis.grammarErrors.length === 0 ? (
                  <div className="text-center py-8 space-y-2">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-600" />
                    <div className="font-medium">No grammar errors found!</div>
                    <div className="text-sm text-muted-foreground">
                      Your text appears to be grammatically correct.
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {analysis.grammarErrors.map((error, index) => (
                        <div key={index} className="border rounded p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityColor(error.severity)} className="flex items-center gap-1">
                              {getSeverityIcon(error.severity)}
                              {error.severity}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{error.position}</span>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-red-600">
                              {error.error}
                            </div>
                            <div className="text-sm text-green-600">
                              <strong>Suggestion:</strong> {error.suggestion}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="style">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Style Suggestions</CardTitle>
              </CardHeader>
              <CardContent>
                {analysis.styleIssues.length === 0 ? (
                  <div className="text-center py-8 space-y-2">
                    <Target className="h-12 w-12 mx-auto text-green-600" />
                    <div className="font-medium">Great style!</div>
                    <div className="text-sm text-muted-foreground">
                      No major style issues detected.
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {analysis.styleIssues.map((issue, index) => (
                        <div key={index} className="border rounded p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityColor(issue.severity)} className="flex items-center gap-1">
                              {getSeverityIcon(issue.severity)}
                              {issue.severity}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {issue.type}
                            </Badge>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="text-sm font-medium">
                              {issue.issue}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              <strong>Suggestion:</strong> {issue.suggestion}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="readability">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Readability Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Readability Score */}
                <div className="text-center space-y-2">
                  <div className="text-3xl font-bold text-primary">
                    {Math.round(analysis.readabilityMetrics.readabilityScore * 100)}%
                  </div>
                  <div className="text-lg font-medium">Readability Score</div>
                  <Progress value={analysis.readabilityMetrics.readabilityScore * 100} className="w-32 mx-auto" />
                </div>

                {/* Detailed Metrics */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Reading Level
                    </span>
                    <span className="font-medium">{analysis.readabilityMetrics.readingLevel}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Average Sentence Length</span>
                    <span className="font-medium">{analysis.readabilityMetrics.averageSentenceLength} words</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Complex Words</span>
                    <span className="font-medium">{analysis.readabilityMetrics.complexWords}</span>
                  </div>
                </div>

                {/* Readability Tips */}
                <div className="space-y-2 p-4 bg-muted rounded-lg">
                  <div className="font-medium">Readability Tips</div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>• Aim for 15-20 words per sentence for optimal readability</div>
                    <div>• Use shorter words when possible without losing meaning</div>
                    <div>• Vary sentence length to maintain reader interest</div>
                    <div>• Break up long paragraphs into smaller chunks</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}